import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import {translateOutcomeTreeData} from "../treeUtils"
import { useHistory } from "react-router-dom";
import axios from "axios"
import { CircularProgress } from '@material-ui/core';
import {decisionReducer} from "../../../Utils/requestUtils"
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from '@material-ui/icons/Clear';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../context/auth-context";
import MarkdownRenderer from "../MarkdownRenderer";
import QiaAvatar from "../QiaAvatar";
import QiaLoader from "../QiaLoader";
import "../ruleAgentTheme.css";
import {
    buildRuleChangePrompt,
    extractRuleChangeSummary,
    getRuleAgentDisplayText,
    openRuleAgentSession,
    streamRuleChangeTurn
} from "../ruleAgentClient";

const getConfirmationIcon = (success) => {
    if (success){
        return (<CheckIcon className="fraudStatusIcon" style={{color: '#73C580', width:"40px", height:"40px"}}/>)
    }
    else{
        return (<ClearIcon className="fraudStatusIcon" style={{color: '#D91A1A', width:"40px", height:"40px"}}/>)
    }
  }

const styles = (theme) => ({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

function SaveRuleDialog(props) {
    const { t } = useTranslation();
    let {ruleDefinition, userVariablesAvailable } = props

    const history = useHistory()
    const { keycloak_adapter, user_data } = useContext(AuthContext)
    let hasAgentRole = ((user_data && user_data.roles) || []).includes("rule_engine_agent_user")
    let agentEnabled = ruleDefinition.rule_key !== "new_rule" && hasAgentRole
    const [ruleDescription, setRuleDescription] = useState("")
    const [isWaitingApproval, setIsWaitingApproval] = useState(false)

    const [dialogState, dispatchDialogState] = useReducer(
        decisionReducer,
        {isLoading: false, isError: false, finished:false}
    )

    const [agentExplanationState, setAgentExplanationState] = useState({status: "idle", text: "", errorMessage: null})
    const fullAgentTextRef = useRef("")
    const abortControllerRef = useRef(null)

    const runAgentExplanationFlow = () => {
        if (!agentEnabled) return

        abortControllerRef.current && abortControllerRef.current.abort()
        let abortController = new AbortController()
        abortControllerRef.current = abortController
        fullAgentTextRef.current = ""
        setAgentExplanationState({status: "loading", text: "", errorMessage: null})

        let token = keycloak_adapter && keycloak_adapter.token
        if (!token) {
            setAgentExplanationState({status: "error", text: "", errorMessage: "missing_token"})
            return
        }

        let oldRule = {tree_data: ruleDefinition.tree_data}
        let newRule = {tree_data: translateOutcomeTreeData(props.treeData)}
        let prompt = buildRuleChangePrompt({oldRule, newRule})

        openRuleAgentSession({token, signal: abortController.signal})
            .then((sessionId) => {
                if (abortController.signal.aborted) return
                // Stay in "loading" until the first delta arrives — the session
                // open call is fast, so switching to an empty "streaming" state
                // here would blank the loader before any text is shown.
                return streamRuleChangeTurn({
                    sessionId,
                    token,
                    prompt,
                    signal: abortController.signal,
                    onDelta: (data) => {
                        if (abortController.signal.aborted) return
                        fullAgentTextRef.current += data.text || ""
                        setAgentExplanationState({
                            status: "streaming",
                            text: getRuleAgentDisplayText(fullAgentTextRef.current),
                            errorMessage: null
                        })
                    },
                    onDone: () => {
                        if (abortController.signal.aborted) return
                        let summary = extractRuleChangeSummary(fullAgentTextRef.current)
                        if (summary) setRuleDescription(summary)
                        setAgentExplanationState({
                            status: "success",
                            text: getRuleAgentDisplayText(fullAgentTextRef.current),
                            errorMessage: null
                        })
                    },
                    onError: (data) => {
                        if (abortController.signal.aborted) return
                        setAgentExplanationState({status: "error", text: "", errorMessage: (data || {}).message})
                    }
                })
            })
            .catch((error) => {
                if (abortController.signal.aborted) return
                setAgentExplanationState({status: "error", text: "", errorMessage: error.message})
            })
    }

    useEffect(() => {
        if (props.open && agentEnabled) {
            runAgentExplanationFlow()
        } else {
            fullAgentTextRef.current = ""
            setAgentExplanationState({status: "idle", text: "", errorMessage: null})
        }
        return () => {
            abortControllerRef.current && abortControllerRef.current.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open])

    const handleCloseDialog = () => {
        props.onClose()
        setTimeout(function (){dispatchDialogState({type:"default_state"})},500)
    }

    const handleFinish = () =>{
        dispatchDialogState({type:"send_request_init"})
        let finalTreeData = translateOutcomeTreeData(props.treeData)
        let payload={
            name:ruleDefinition.name,
            description: ruleDescription,
            tree_data:finalTreeData,
            user_variables: userVariablesAvailable
        }
        if (ruleDefinition.rule_key === "new_rule") {
            let headersRequest = {
                headers: {
                    "COMPANY-KEY": ruleDefinition.company_key
                }
            }
            payload.event_type = ruleDefinition.event_type
            setTimeout(function () {
                let url = '/dash/rule_engine/rule?environment=' + ruleDefinition.environment
                axios.post(url, payload, headersRequest).then(response => {
                    dispatchDialogState({type:"send_request_success"})
                  }).catch(error => {
                    if ((error.response || {}).status === 403) dispatchDialogState({type: "send_request_failure_403"})
                    else if ((error.response || {}).status === 409) dispatchDialogState({type: "send_request_failure_409"})  
                    else dispatchDialogState({type: "send_request_failure"})    
                  });
            }, 1000);            
        }
        // delete payload 
        else{
            let headersRequest = {
                headers: {
                    "COMPANY-KEY": ruleDefinition.company_key
                }
            }
            setTimeout(function () {
                axios.put('/dash/rule_engine/rule/' + ruleDefinition.rule_key + "?environment=" + ruleDefinition.environment, payload, headersRequest).then(response => {
                    if (response.data?.status === "waiting_for_approval") {
                        setIsWaitingApproval(true)
                    } else {
                        dispatchDialogState({type:"send_request_success"})
                    }
                  }).catch(error => {
                    if ((error.response || {}).status === 403) dispatchDialogState({type: "send_request_failure_403"})
                    else if ((error.response || {}).status === 409) dispatchDialogState({type: "send_request_failure_409"})
                    else dispatchDialogState({type: "send_request_failure"})
                  });
            }, 1000);
        }

    }

    const handleAlert = () => {
        alert("rule_change_save_message")
    }

    if (isWaitingApproval) {
        return (
            <Dialog
                open={props.open}
                onClose={() => history.push("/rules-inbox")}
                keepMounted={false}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <DialogTitle style={{paddingBottom: "0px"}} className={["blueText", "normalMediumSize"].join(" ")}>{t("Regra Enviada para Aprovação")}</DialogTitle>
                <DialogContent>
                    <div style={{width: "310px", height:"217px"}}>
                        <div className="internalCardContainer" style={{display:"flex", height:"40%", justifyContent:"center"}}>
                            <CheckIcon className="fraudStatusIcon" style={{color: '#73C580', width:"40px", height:"40px"}}/>
                        </div>
                        <div className="internalCardContainer" style={{display:"flex", textAlign:"center", justifyContent:"center"}}>
                            {t("A alteração foi enviada para aprovação. A regra deve ser revisada antes de ser atualizada.")}
                        </div>
                        <div className="internalCardContainer" style={{display:"flex", justifyContent:"center"}}>
                            <div
                                className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                onClick={() => history.push("/rules-inbox")}
                            >
                                {t("VOLTAR À TELA INICIAL")}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        )
    }

    if (dialogState.finished && dialogState.isLoading){
        return (
            <Dialog 
                open={props.open} 
                onClose={() => history.push("/rules-inbox")} 
                keepMounted={false}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <DialogTitle style={{paddingBottom: "0px"}} className={["blueText", "normalMediumSize"].join(" ")}>{t("Salvando Regra")}</DialogTitle>
                <DialogContent>
                    <div style={{display:"flex", width: "310px", height:"217px"}}>
                        <CircularProgress style={{margin:"auto"}} />
                    </div>
                </DialogContent>
            </Dialog>

        )
    }
    else if (dialogState.finished){
        let confirmationData = {
            message: dialogState.message,
            success: !dialogState.isError
        }
        return (
            <Dialog 
                open={props.open} 
                onClose={confirmationData.success ? () => history.push("/rules-inbox") : handleCloseDialog} 
                keepMounted={false}
                disableBackdropClick
            >
                <DialogTitle style={{paddingBottom: "0px"}} className={["blueText", "normalMediumSize"].join(" ")}>{t("Confirmação de Revisão")}</DialogTitle>
                <DialogContent>
                <div style={{width: "310px", height:"217px"}}>
                    <div className="internalCardContainer" style={{display:"flex", height:"40%", justifyContent:"center"}}>
                        {getConfirmationIcon(confirmationData.success)}
                    </div>
                    <div className="internalCardContainer" style={{display:"flex", textAlign:"center", justifyContent:"center"}}>
                        {confirmationData.message}
                    </div>
                    <div className="internalCardContainer" style={{display:"flex", justifyContent:"center"}}>
                        <div 
                            className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                            onClick={confirmationData.success ? () => history.push("/rules-inbox") : handleCloseDialog}
                        >
                            {confirmationData.success ? t("VOLTAR À TELA INICIAL") : "FECHAR"}
                        </div>
                    </div>
                </div>
                </DialogContent>
            </Dialog>
        )

    }
    return (
        <Dialog 
        open={props.open} 
        onClose={handleCloseDialog} 
        keepMounted={false}
        >
            <DialogTitle style={{paddingBottom: "0px"}} className={["blueText", "normalMediumSize"].join(" ")}>{t("Salvar Regra")}</DialogTitle>
            <DialogContent style={{paddingBottom: agentEnabled ? "40px" : "180px"}}>
                <div
                    style={{
                        width: agentEnabled ? "460px" : "366px",
                        height: agentEnabled ? "auto" : "160px",
                        display:"flex", flexDirection:"column",
                        marginBottom:"30px", alignItems:"center"
                    }}
                >
                    <div
                        className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")}
                        style={{width:"90%", textAlign:"center"}}>
                        {t("A versão corrente da regra será substituída pela árvore atual!")} <strong>{t("Deseja mesmo continuar?")}</strong>
                    </div>
                    {agentEnabled &&
                        <div
                            className="internalCardContainer qia-ai-panel qia-fadeup"
                            style={{width:"90%", textAlign:"left", boxSizing:"border-box"}}
                        >
                            <div className="qia-ai-panel-title">
                                <QiaAvatar size={20} />
                                <span className="qia-gradient-title">{t("Análise da alteração (IA)")}</span>
                            </div>
                            {agentExplanationState.status === "loading" &&
                                <div className="qia-ai-loading">
                                    <QiaLoader label={t("Analisando a alteração...")} size={40} />
                                </div>
                            }
                            {(agentExplanationState.status === "streaming" || agentExplanationState.status === "success") &&
                                <div className="qia-bot-bubble qia-ai-content qia-fadeup">
                                    <MarkdownRenderer source={agentExplanationState.text} />
                                </div>
                            }
                            {agentExplanationState.status === "error" &&
                                <div className="qia-ai-error qia-fadeup" style={{textAlign:"center"}}>
                                    <div className={["normalText", "normalMediumSize"].join(" ")} style={{marginBottom:"8px"}}>
                                        {t("Não foi possível gerar a explicação automática.")}
                                    </div>
                                    <div
                                        className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                                        style={{display:"inline-block", padding:"4px 16px", width:"auto"}}
                                        onClick={runAgentExplanationFlow}
                                    >
                                        {t("Tentar novamente")}
                                    </div>
                                </div>
                            }
                        </div>
                    }
                    <div
                        className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")}
                        style={{width:"90%"}}>
                        {t("Para continuar, inclua a descrição da alteração:")}
                    </div>
                    <div style={{width: "90%", marginBottom: "15px"}}>
                        <TextField
                            error={!ruleDescription.length > 0}
                            label={t("Descrição da Alteração")}
                            style={{textAlign:"center"}} 
                            className="filterTextbox" 
                            size="small" fullWidth 
                            value={ruleDescription}
                            multiline
                            onChange={(e) => setRuleDescription(e.target.value)}
                            required
                        />  
                    </div>
                    <div className="internalCardContainer" style={{display:"flex", marginTop: "10px"}}>
                        <div
                            className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                            style={{width:"120px"}}
                            onClick={handleCloseDialog}
                        >
                            {t("Cancelar")}
                        </div>
                        {ruleDescription ?
                        <div
                            className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                            style={{width:"120px"}}
                            onClick={handleFinish}
                        >
                            {t("save")}
                        </div>  :
                        <div
                            className={["button", "standard", "disabled", "normalText", "normalMediumSize"].join(" ")}
                            style={{width:"120px"}}
                            onClick={handleAlert}

                        >
                            {t("save")}
                        </div>}   
                    </div>
                </div>
          </DialogContent>
        </Dialog>
    )
}

export default SaveRuleDialog;